// src/pages/performance/ManageAppraisalsDetailsDrawer.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/api";

import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
} from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";

import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import TimelineIcon from "@mui/icons-material/Timeline";
import ShieldIcon from "@mui/icons-material/Shield";
import StarIcon from "@mui/icons-material/Star";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import StatusChip from "./ManageAppraisalsStatusChip";
import { API } from "./manageAppraisals.constants";
import { normalizeSingleResponse, normalizeToArray, safeMsg } from "./manageAppraisals.utils";

/* ---------------------------------------
   Small clean styles
---------------------------------------- */
const DrawerPaper = styled(Paper)(({ theme }) => ({
  height: "100%",
  background: theme.palette.background.default,
}));

const HeaderWrap = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.92 : 0.88),
  backdropFilter: "blur(10px)",
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
}));

const AccentLine = styled("div")(({ theme }) => ({
  height: 3,
  width: "100%",
  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
}));

const Section = styled(Paper)(({ theme }) => ({
  borderRadius: 14,
  border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
  boxShadow: "none",
  background: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.8 : 0.7),
  overflow: "hidden",
}));

const ActionIcon = styled(IconButton)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
  background: alpha(theme.palette.background.paper, 0.55),
  "&:hover": { background: alpha(theme.palette.background.paper, 0.85) },
}));

/* ---------------------------------------
   Helpers
---------------------------------------- */
const pick = (...vals) => vals.find((v) => v !== undefined && v !== null);

const fmtDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isDone = (s) => ["completed", "submitted", "approved", "locked"].includes(String(s || "").toLowerCase());
const isInProgress = (s) => ["in_progress"].includes(String(s || "").toLowerCase());
const isPending = (s) => ["pending", "not_started", "draft"].includes(String(s || "").toLowerCase());
const isFinalizedStep = (s) =>
  ["submitted", "completed", "approved", "locked"].includes(String(s || "").toLowerCase());

/**
 * Normalize actor types to the canonical values you should send to backend.
 * This avoids 422 when backend validates actor_type using an enum list.
 */
const normalizeActorTypeCanonical = (t) => {
  const x = String(t || "").toLowerCase().trim();

  if (["specific_user", "user"].includes(x)) return "specific_user";

  if (["self", "employee", "employee_self"].includes(x)) return "self";

  if (["direct_manager", "manager", "supervisor", "line_manager"].includes(x)) return "direct_manager";

  if (["second_level", "hod", "head_of_department"].includes(x)) return "second_level";

  if (["operations_manager", "operation_manager", "ops_manager", "operations"].includes(x)) return "operations_manager";

  if (["hr_admin", "admin_hr", "hr"].includes(x)) return "hr_admin";

  if (["hr_team", "hr_review"].includes(x)) return "hr_team";

  // many flows store CEO as final_reviewer in backend
  if (["final_reviewer", "ceo"].includes(x)) return "final_reviewer";

  // fallback safe
  return "specific_user";
};

const roleLabelFromActor = (actorType) => {
  const t = normalizeActorTypeCanonical(actorType);
  if (t === "direct_manager") return "Direct Manager";
  if (t === "self") return "Employee";
  if (t === "second_level") return "Second Level";
  if (t === "operations_manager") return "Operation Manager";
  if (t === "hr_admin") return "HR Admin";
  if (t === "hr_team") return "HR Team";
  if (t === "final_reviewer") return "CEO / Final Reviewer";
  if (t === "specific_user") return "Specific User";
  return actorType ? String(actorType) : "—";
};

const extractSteps = (appraisal) => {
  const raw = pick(
    appraisal?.stepInstances,
    appraisal?.step_instances,
    appraisal?.stepinstances,
    appraisal?.steps,
    appraisal?.workflow_steps,
    appraisal?.workflowSteps,
    appraisal?.workflowStepInstances
  );
  if (!raw) return [];
  return Array.isArray(raw) ? raw : [];
};

const extractEmployee = (appraisal) => pick(appraisal?.employee, appraisal?.Employee, appraisal?.emp, appraisal?.user) || {};

const extractWorkflowName = (appraisal) =>
  pick(
    appraisal?.workflowTemplate?.name,
    appraisal?.workflow_template?.name,
    appraisal?.workflow_template_name,
    appraisal?.workflowName,
    appraisal?.workflow_name
  ) || "—";

const extractOverallRating = (appraisal) =>
  pick(appraisal?.overall_rating, appraisal?.overallRating, appraisal?.overall_score, appraisal?.overallScore, appraisal?.overall) ?? "—";

const extractCurrentStepOrder = (appraisal) => pick(appraisal?.current_step_order, appraisal?.currentStepOrder, appraisal?.current_step, appraisal?.currentStep) ?? null;

const extractUserFromStep = (s) => {
  const user = pick(s?.user, s?.assigned_user, s?.assignedUser, s?.evaluator, s?.actor_user);
  return user || null;
};

const extractWorkflowStepId = (s) =>
  pick(
    s?.workflow_step_id,
    s?.workflowStepId,
    s?.workflow_step?.id,
    s?.workflowStep?.id
  ) ?? null;

const formatLaravel422 = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;

  if (status !== 422) return null;

  // Common Laravel shapes:
  // { message: "...", errors: { field: ["msg"] } }
  if (data?.errors && typeof data.errors === "object") {
    const flat = Object.values(data.errors).flat().filter(Boolean);
    if (flat.length) return flat.join(" • ");
  }
  if (data?.message) return String(data.message);
  return "Validation failed (422). Please check required fields.";
};

export function AppraisalDetailsDrawer({ open, appraisalId, onClose, onRefresh, onRequestEdit, onRequestDelete }) {
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [appraisal, setAppraisal] = useState(null);
  const [error, setError] = useState("");
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [assigneeDraftByStep, setAssigneeDraftByStep] = useState({});
  const [stepActionBusyKey, setStepActionBusyKey] = useState("");

  const fetchDetails = useCallback(async () => {
    if (!appraisalId) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`${API.appraisals}/${appraisalId}`);
      const data = normalizeSingleResponse(res);
      setAppraisal(data);
    } catch (err) {
      setError(safeMsg(err, "Failed to load appraisal details"));
    } finally {
      setLoading(false);
    }
  }, [appraisalId]);

  const fetchAssignableUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const res = await api.get(API.adminUsers, { params: { per_page: 500 } });
      const list = normalizeToArray(res);

      const mapped = (Array.isArray(list) ? list : [])
        .map((u) => {
          const id = Number(u?.id);
          if (!Number.isFinite(id) || id <= 0) return null;
          const name = String(u?.name || "User");
          const code = String(u?.employee_code || "").trim();
          const role = String(u?.actual_role || u?.role || "").trim();
          const bits = [name, code ? `(${code})` : "", role ? `- ${role}` : ""].filter(Boolean).join(" ");
          return { id, label: bits || `User #${id}` };
        })
        .filter(Boolean);

      const uniq = [];
      const seen = new Set();
      mapped.forEach((x) => {
        if (seen.has(x.id)) return;
        seen.add(x.id);
        uniq.push(x);
      });

      setUserOptions(uniq);
    } catch {
      setUserOptions([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchDetails();
      fetchAssignableUsers();
    }
    else {
      setAppraisal(null);
      setError("");
      setCustomizeOpen(false);
      setAssigneeDraftByStep({});
      setStepActionBusyKey("");
    }
  }, [open, fetchDetails, fetchAssignableUsers]);

  const stepsRaw = useMemo(() => extractSteps(appraisal), [appraisal]);

  const steps = useMemo(() => {
    return stepsRaw
      .slice()
      .map((s, idx) => {
        const stepOrder = Number(pick(s.step_order, s.stepOrder, s.order, s.sequence, idx + 1)) || idx + 1;
        const stepName = pick(s.step_name, s.stepName, s.title, s.name, s.label) || "—";
        const actorTypeRaw = pick(s.actor_type, s.actorType, s.step_actor_type, s.stepActorType) || "specific_user";
        const actorType = normalizeActorTypeCanonical(actorTypeRaw);

        const user = extractUserFromStep(s);
        const userId = pick(s.user_id, s.userId, s.assigned_user_id, s.assignedUserId, user?.id) ?? null;

        const responses = Array.isArray(s.responses) ? s.responses : Array.isArray(s.step_responses) ? s.step_responses : [];

        return {
          ...s,
          step_order: stepOrder,
          step_name: stepName,
          actor_type: actorType,
          user,
          user_id: userId,
          responses,
        };
      })
      .sort((a, b) => (Number(a.step_order) || 0) - (Number(b.step_order) || 0));
  }, [stepsRaw]);

  const currentStepOrder = useMemo(() => extractCurrentStepOrder(appraisal), [appraisal]);

  const currentStep = useMemo(() => {
    if (!steps.length) return null;

    if (currentStepOrder != null) {
      const byOrder = steps.find((s) => Number(s.step_order) === Number(currentStepOrder));
      if (byOrder) return byOrder;
    }

    return (
      steps.find((s) => isInProgress(s.status)) ||
      steps.find((s) => String(s.status || "").toLowerCase() === "pending") ||
      steps.find((s) => isPending(s.status)) ||
      steps[0] ||
      null
    );
  }, [steps, currentStepOrder]);

  const nextStep = useMemo(() => {
    if (!steps.length || !currentStep) return null;
    const currOrder = Number(currentStep.step_order) || 0;
    return steps.find((s) => isPending(s.status) && Number(s.step_order) > currOrder) || null;
  }, [steps, currentStep]);

  const stepStats = useMemo(() => {
    const total = steps.length || 0;
    const done = steps.filter((s) => isDone(s.status)).length;
    const inProgress = steps.filter((s) => isInProgress(s.status)).length;
    const pending = Math.max(0, total - done - inProgress);
    const progress = total ? Math.round((done / total) * 100) : 0;
    return { total, done, inProgress, pending, progress };
  }, [steps]);

  const accessList = useMemo(() => {
    if (!appraisal) return [];

    const list = [];
    const employee = extractEmployee(appraisal);
    if (employee?.name || employee?.employee_code) {
      list.push({
        type: "employee",
        label: `${employee?.name || "—"} (${employee?.employee_code || "—"})`,
      });
    }

    const seen = new Set();
    steps.forEach((s) => {
      const u = s?.user;
      const uid = u?.id ?? s?.user_id ?? null;
      if (uid && !seen.has(uid)) {
        seen.add(uid);
        list.push({
          type: "evaluator",
          label: `${u?.name || "User"} (${u?.employee_code || "—"})`,
        });
      }
    });

    return list;
  }, [appraisal, steps]);

  const isCustomized = !!pick(appraisal?.is_workflow_customized, appraisal?.workflow_is_customized, appraisal?.isWorkflowCustomized);

  const workflowName = useMemo(() => extractWorkflowName(appraisal), [appraisal]);
  const employee = useMemo(() => extractEmployee(appraisal), [appraisal]);
  const overallRating = useMemo(() => extractOverallRating(appraisal), [appraisal]);
  const canManageOngoingSteps = useMemo(() => {
    const s = String(appraisal?.status || "").toLowerCase();
    return !!appraisalId && !["completed", "approved", "rejected", "cancelled"].includes(s);
  }, [appraisal, appraisalId]);

  useEffect(() => {
    const next = {};
    steps.forEach((s) => {
      if (s?.id == null) return;
      next[String(s.id)] = s?.user_id ?? "";
    });
    setAssigneeDraftByStep(next);
  }, [steps]);

  const handleAssignStep = useCallback(
    async (step) => {
      const stepId = Number(step?.id);
      if (!appraisalId || !Number.isFinite(stepId) || stepId <= 0) return;

      const selected = Number(assigneeDraftByStep[String(stepId)] ?? 0);
      if (!Number.isFinite(selected) || selected <= 0) {
        setError("Please choose an assignee first.");
        return;
      }

      setError("");
      setStepActionBusyKey(`assign:${stepId}`);
      try {
        await api.post(API.assignStepAssignee(appraisalId, stepId), { user_id: selected });
        await fetchDetails();
        onRefresh?.();
      } catch (err) {
        setError(formatLaravel422(err) || safeMsg(err, "Failed to assign step"));
      } finally {
        setStepActionBusyKey("");
      }
    },
    [appraisalId, assigneeDraftByStep, fetchDetails, onRefresh]
  );

  const handleSkipStep = useCallback(
    async (step) => {
      const stepId = Number(step?.id);
      if (!appraisalId || !Number.isFinite(stepId) || stepId <= 0) return;

      const stepLabel = `Step #${step?.step_order ?? "?"} - ${step?.step_name || step?.title || "Unnamed step"}`;
      const ok = window.confirm(`Skip ${stepLabel}?`);
      if (!ok) return;

      setError("");
      setStepActionBusyKey(`skip:${stepId}`);
      try {
        await api.post(API.skipStep(appraisalId, stepId), {});
        await fetchDetails();
        onRefresh?.();
      } catch (err) {
        setError(formatLaravel422(err) || safeMsg(err, "Failed to skip step"));
      } finally {
        setStepActionBusyKey("");
      }
    },
    [appraisalId, fetchDetails, onRefresh]
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => !loading && onClose?.()}
      PaperProps={{
        component: DrawerPaper,
        sx: { width: { xs: "100%", sm: 610 }, maxWidth: "100vw" },
      }}
    >
      {/* Header */}
      <HeaderWrap>
        <AccentLine />
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 950, lineHeight: 1.15 }}>
                Appraisal Details
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
                <Chip size="small" variant="outlined" label={`#${appraisalId || "—"}`} sx={{ borderRadius: 999, fontWeight: 800 }} />
                {isCustomized ? <Chip size="small" color="warning" label="Custom workflow" sx={{ borderRadius: 999, fontWeight: 900 }} /> : null}
                {appraisal?.status ? <StatusChip value={appraisal.status} /> : null}
              </Stack>
            </Box>

            <Stack direction="row" spacing={0.75} alignItems="center">
              <Tooltip title="Edit appraisal">
                <span>
                  <ActionIcon onClick={() => onRequestEdit?.()} disabled={!appraisalId}>
                    <EditIcon fontSize="small" />
                  </ActionIcon>
                </span>
              </Tooltip>

              <Tooltip title="Customize workflow (add/remove/reorder/assign)">
                <span>
                  <ActionIcon onClick={() => setCustomizeOpen(true)} disabled={!appraisalId || loading}>
                    <TuneIcon fontSize="small" />
                  </ActionIcon>
                </span>
              </Tooltip>

              <Tooltip title="Delete appraisal">
                <span>
                  <ActionIcon color="error" onClick={() => onRequestDelete?.()} disabled={!appraisalId}>
                    <DeleteOutlineIcon fontSize="small" />
                  </ActionIcon>
                </span>
              </Tooltip>

              <Tooltip title="Close">
                <ActionIcon onClick={() => !loading && onClose?.()}>
                  <CloseIcon fontSize="small" />
                </ActionIcon>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Quick progress */}
          <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                Progress
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {stepStats.done}/{stepStats.total} steps completed
              </Typography>
              <Chip
                size="small"
                label={`${stepStats.progress}%`}
                sx={{
                  ml: "auto",
                  borderRadius: 999,
                  fontWeight: 900,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: "primary.main",
                }}
              />
            </Stack>

            <LinearProgress
              variant="determinate"
              value={stepStats.progress}
              sx={{
                mt: 0.75,
                height:8,
                borderRadius: 999,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                "& .MuiLinearProgress-bar": { borderRadius: 999 },
              }}
            />
          </Box>
        </Box>

        {loading ? <LinearProgress /> : null}
      </HeaderWrap>

      {/* Body */}
      <Box sx={{ p: 2 }}>
        {!!error && (
          <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
            {error}
          </Alert>
        )}

        {!error && !loading && !appraisal && (
          <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
            No details available.
          </Alert>
        )}

        {!!appraisal && (
          <Stack spacing={2}>
            {/* Employee */}
            <Section>
              <Box sx={{ p: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar
                    sx={{
                      width: 46,
                      height: 46,
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: "primary.main",
                      fontWeight: 950,
                    }}
                  >
                    {employee?.name?.charAt?.(0) || <PersonIcon />}
                  </Avatar>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: 0.6 }}>
                      Employee
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 950 }} noWrap>
                      {employee?.name || "—"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                      {employee?.employee_code || "—"} • {employee?.department_name || employee?.department || "—"}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                  <Chip size="small" label={`Role: ${employee?.actual_role || employee?.role || "—"}`} sx={{ borderRadius: 999 }} />
                  <Chip size="small" label={`Dept: ${employee?.department_name || employee?.department || "—"}`} sx={{ borderRadius: 999 }} />
                </Stack>
              </Box>
            </Section>

            {/* Workflow */}
            <Section>
              <Box sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <AccountTreeIcon sx={{ color: "text.secondary" }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: 0.6 }}>
                      Workflow
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 950 }} noWrap>
                      {workflowName}
                    </Typography>
                  </Box>
                  {appraisal?.status ? <StatusChip value={appraisal.status} /> : null}
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  <Chip
                    size="small"
                    icon={<TimelineIcon fontSize="small" />}
                    label={`Current Step: ${currentStepOrder ?? currentStep?.step_order ?? "—"}`}
                    sx={{ borderRadius: 999 }}
                  />
                  <Chip
                    size="small"
                    icon={<StarIcon fontSize="small" />}
                    label={`Overall Rating: ${overallRating}`}
                    sx={{ borderRadius: 999 }}
                  />
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                      Currently evaluating
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 900, mt: 0.25 }}>
                      {currentStep?.user?.name
                        ? `${currentStep.user.name} (${currentStep.user.employee_code || "—"})`
                        : currentStep
                        ? "Unassigned"
                        : "None"}
                    </Typography>
                    {currentStep ? (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Step #{currentStep.step_order ?? "—"} • {roleLabelFromActor(currentStep.actor_type)} • Status:{" "}
                        {String(currentStep.status || "—").replaceAll("_", " ")}
                      </Typography>
                    ) : null}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                      Next in workflow
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 900, mt: 0.25 }}>
                      {nextStep?.user?.name ? `${nextStep.user.name} (${nextStep.user.employee_code || "—"})` : nextStep ? "Unassigned" : "—"}
                    </Typography>
                    {nextStep ? (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Step #{nextStep.step_order ?? "—"} • {roleLabelFromActor(nextStep.actor_type)} • Status:{" "}
                        {String(nextStep.status || "pending").replaceAll("_", " ")}
                      </Typography>
                    ) : null}
                  </Grid>
                </Grid>
              </Box>
            </Section>

            {/* Access */}
            <Section>
              <Box sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <ShieldIcon sx={{ color: "text.secondary" }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                    Who has access
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {accessList.map((item, idx) => {
                    const tint = item.type === "employee" ? theme.palette.info.main : theme.palette.primary.main;

                    return (
                      <Chip
                        key={`${item.type}-${idx}`}
                        size="small"
                        label={item.type === "employee" ? `Employee: ${item.label}` : `Evaluator: ${item.label}`}
                        sx={{
                          borderRadius: 999,
                          bgcolor: alpha(tint, 0.08),
                          border: `1px solid ${alpha(tint, 0.18)}`,
                          fontWeight: 800,
                        }}
                      />
                    );
                  })}
                </Stack>

                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                  Plus HR/admin roles as per system permissions.
                </Typography>
              </Box>
            </Section>

            {/* Steps */}
            <Section>
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 950, mb: 1 }}>
                  Steps & Comments
                </Typography>

                {steps.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No steps found.
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {steps.map((s, idx) => {
                      const responses = Array.isArray(s.responses) ? s.responses : [];
                      const comments = responses.filter((r) => r?.comments && typeof r.comments === "string" && r.comments.trim() !== "");

                      const lastTs =
                        s.submitted_at ||
                        s.updated_at ||
                        s.completed_at ||
                        (s.latest_submission || s.latestSubmission)?.submitted_at ||
                        (s.latest_submission || s.latestSubmission)?.updated_at ||
                        null;

                      const commentPreview = comments[0]?.comments
                        ? comments[0].comments.length > 220
                          ? comments[0].comments.slice(0, 220) + "…"
                          : comments[0].comments
                        : "";

                      const isActive = currentStepOrder != null ? Number(s.step_order) === Number(currentStepOrder) : false;
                      const stepStatus = String(s.status || "").toLowerCase();
                      const isPastStep =
                        currentStepOrder != null ? Number(s.step_order) < Number(currentStepOrder) : false;
                      const isSkippedStep = stepStatus === "skipped";
                      const canAssignStep =
                        canManageOngoingSteps && !isFinalizedStep(stepStatus) && !isPastStep;
                      const canSkipStep =
                        canManageOngoingSteps && !isFinalizedStep(stepStatus) && !isSkippedStep && !isPastStep;
                      const stepId = Number(s?.id);
                      const stepKey = Number.isFinite(stepId) && stepId > 0 ? String(stepId) : "";
                      const assignBusy = !!stepKey && stepActionBusyKey === `assign:${stepKey}`;
                      const skipBusy = !!stepKey && stepActionBusyKey === `skip:${stepKey}`;
                      const selectedAssignee = stepKey ? assigneeDraftByStep[stepKey] ?? "" : "";

                      return (
                        <Accordion
                          key={s.id || `${s.step_order}-${idx}`}
                          disableGutters
                          elevation={0}
                          sx={{
                            borderRadius: 12,
                            border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
                            overflow: "hidden",
                            background: alpha(theme.palette.background.paper, 0.55),
                            "&:before": { display: "none" },
                            ...(isActive && {
                              borderColor: alpha(theme.palette.primary.main, 0.3),
                              background: alpha(theme.palette.primary.main, 0.04),
                            }),
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ width: "100%" }}>
                              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 950 }} noWrap>
                                    Step #{s.step_order ?? "—"} — {s.step_name || s.title || "—"}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
                                    {s.user?.name ? `${s.user.name} (${s.user.employee_code || "—"})` : "Unassigned"}
                                    {" • "}
                                    {roleLabelFromActor(s.actor_type)}
                                    {lastTs ? ` • ${fmtDateTime(lastTs)}` : ""}
                                  </Typography>
                                </Box>

                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                                  {comments.length > 0 ? (
                                    <Chip size="small" label={`Comments: ${comments.length}`} sx={{ borderRadius: 999, fontWeight: 800 }} />
                                  ) : null}
                                  <StatusChip value={s.status} />
                                </Stack>
                              </Stack>
                            </Box>
                          </AccordionSummary>

                          <AccordionDetails sx={{ pt: 0 }}>
                            <Divider sx={{ mb: 1.25 }} />

                            <Grid container spacing={1.25}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                  Assigned to
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                  {s.user?.name ? `${s.user.name} (${s.user.employee_code || "—"})` : "Unassigned"}
                                </Typography>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                  Actor type
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                  {roleLabelFromActor(s.actor_type)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  {s.actor_type ? `(${String(s.actor_type)})` : ""}
                                </Typography>
                              </Grid>

                              <Grid item xs={12} sm={8}>
                                <TextField
                                  select
                                  size="small"
                                  fullWidth
                                  label="Change assignee"
                                  value={selectedAssignee}
                                  onChange={(e) =>
                                    setAssigneeDraftByStep((prev) => ({
                                      ...prev,
                                      [stepKey]: e.target.value ? Number(e.target.value) : "",
                                    }))
                                  }
                                  disabled={!stepKey || !canAssignStep || usersLoading || assignBusy || skipBusy}
                                  helperText={
                                    !stepKey
                                      ? "Only saved step instances can be changed."
                                      : isPastStep
                                      ? "Past steps cannot be reassigned."
                                      : usersLoading
                                      ? "Loading users..."
                                      : "Select user then click Assign."
                                  }
                                >
                                  <MenuItem value="">Select assignee</MenuItem>
                                  {userOptions.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>
                                      {u.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>

                              <Grid item xs={12} sm={4}>
                                <Stack direction="row" spacing={1}>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    fullWidth
                                    onClick={() => handleAssignStep(s)}
                                    disabled={
                                      !stepKey ||
                                      !canAssignStep ||
                                      usersLoading ||
                                      !selectedAssignee ||
                                      assignBusy ||
                                      skipBusy
                                    }
                                    sx={{ textTransform: "none", fontWeight: 900, boxShadow: "none" }}
                                  >
                                    {assignBusy ? <CircularProgress size={16} /> : "Assign"}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    fullWidth
                                    onClick={() => handleSkipStep(s)}
                                    disabled={!stepKey || !canSkipStep || assignBusy || skipBusy}
                                    sx={{ textTransform: "none", fontWeight: 900 }}
                                  >
                                    {skipBusy ? <CircularProgress size={16} /> : "Skip"}
                                  </Button>
                                </Stack>
                              </Grid>
                            </Grid>

                            {commentPreview ? (
                              <Box sx={{ mt: 1.25 }}>
                                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 900 }}>
                                  Latest comment
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary", whiteSpace: "pre-wrap" }}>
                                  {commentPreview}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1.25 }}>
                                No comments for this step.
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Section>

            {/* Bottom actions */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={() => onRefresh?.()}
                disabled={loading}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
              >
                Refresh List
              </Button>
              <Button
                variant="contained"
                onClick={() => !loading && onClose?.()}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900, boxShadow: "none" }}
              >
                Close
              </Button>
            </Box>
          </Stack>
        )}
      </Box>

      <CustomizeWorkflowDialog
        open={customizeOpen}
        appraisal={appraisal}
        onClose={() => setCustomizeOpen(false)}
        onSaved={() => fetchDetails()}
        onError={(msg) => setError(msg)}
        onAfterSave={() => onRefresh?.()}
      />
    </Drawer>
  );
}

/* ---------------------------------------
   Customize Workflow Dialog
   ✅ Fully customizable: add / remove / duplicate / reorder / assign
   ✅ FIX 422: canonical actor_type + always send step_name + send workflow_step_id
   ✅ Better 422 message shown to UI
---------------------------------------- */
function CustomizeWorkflowDialog({ open, appraisal, onClose, onSaved, onError, onAfterSave }) {
  const theme = useTheme();
  const [submitting, setSubmitting] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const [draft, setDraft] = useState([]);

  const appraisalId = appraisal?.id;
  const stepInstances = useMemo(() => extractSteps(appraisal), [appraisal]);

  const hasSubmittedSteps = useMemo(() => {
    return (Array.isArray(stepInstances) ? stepInstances : []).some((s) =>
      ["completed", "submitted", "locked", "approved"].includes(String(s.status || "").toLowerCase())
    );
  }, [stepInstances]);

  const employeeLabel = useMemo(() => {
    const e = extractEmployee(appraisal);
    if (!e) return "";
    return `${e.name || "—"} (${e.employee_code || "—"})`;
  }, [appraisal]);

  useEffect(() => {
    if (!open) return;

    setSubmitting(false);
    onError?.("");

    const base = (Array.isArray(stepInstances) ? stepInstances : [])
      .slice()
      .map((s, idx) => {
        const actorTypeRaw = pick(s.actor_type, s.actorType, s.step_actor_type, s.stepActorType) || "specific_user";
        const actor_type = normalizeActorTypeCanonical(actorTypeRaw);

        const title = pick(s.step_name, s.stepName, s.title, s.name) || roleLabelFromActor(actor_type);
        const u = extractUserFromStep(s);

        const currentUserId = pick(s.user_id, s.userId, s.assigned_user_id, s.assignedUserId, u?.id) ?? null;
        const currentUserLabel = u?.name
          ? `${u.name} (${u.employee_code || "—"})`
          : currentUserId
          ? `User ID: ${currentUserId}`
          : actor_type === "specific_user"
          ? "Unassigned"
          : "Auto";

        const workflow_step_id = extractWorkflowStepId(s);

        return {
          _key: String(s.id ?? `step_${idx}_${Date.now()}`),
          is_new: false,
          step_instance_id: s.id ?? null,
          workflow_step_id: workflow_step_id, // IMPORTANT: used by many backends/DB constraints
          actor_type,
          step_name: title,
          current_user_id: currentUserId,
          current_label: currentUserLabel,
          new_user_id: null, // null means keep current/auto
        };
      });

    // keep same order as received (or by step_order if available)
    base.sort((a, b) => {
      const ao = Number(pick(a.step_order, a.stepOrder, a.order)) || 0;
      const bo = Number(pick(b.step_order, b.stepOrder, b.order)) || 0;
      return ao - bo;
    });

    setDraft(base);

    (async () => {
      setLoadingUsers(true);
      try {
        const res = await api.get(API.adminUsers, { params: { per_page: 500 } });
        const list = normalizeToArray(res);
        setUsers(Array.isArray(list) ? list : []);
      } catch {
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    })();
  }, [open, stepInstances, onError]);

  const userOptions = useMemo(() => {
    return (users || []).map((u) => ({
      id: u.id,
      employee_code: u.employee_code,
      name: u.name,
      actual_role: u.actual_role || u.role || "",
      label: `${u.name || "—"} (${u.employee_code || "—"})${u.actual_role ? ` — ${u.actual_role}` : ""} — ID:${u.id}`,
    }));
  }, [users]);

  // Canonical actor types (match backend expectations)
  const actorTypeOptions = useMemo(
    () => [
      { value: "direct_manager", label: "Direct Manager (Auto)" },
      { value: "self", label: "Employee (Auto)" },
      { value: "second_level", label: "Second Level (Auto)" },
      { value: "operations_manager", label: "Operation Manager (Auto)" },
      { value: "hr_admin", label: "HR Admin (Auto)" },
      { value: "hr_team", label: "HR Team (Auto)" },
      { value: "final_reviewer", label: "CEO / Final Reviewer (Auto)" },
      { value: "specific_user", label: "Specific User (Required)" },
    ],
    []
  );

  const nearestWorkflowStepId = (prev, index) => {
    if (!Array.isArray(prev) || !prev.length) return null;
    // try previous
    if (index >= 0 && prev[index]?.workflow_step_id) return prev[index].workflow_step_id;
    // try next
    if (index + 1 < prev.length && prev[index + 1]?.workflow_step_id) return prev[index + 1].workflow_step_id;
    // fallback first found
    return prev.find((x) => x?.workflow_step_id)?.workflow_step_id ?? null;
  };

  const addStepAt = (index) => {
    setDraft((prev) => {
      const key = `new_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      const wfId = nearestWorkflowStepId(prev, index ?? prev.length - 1);

      const row = {
        _key: key,
        is_new: true,
        step_instance_id: null,
        workflow_step_id: wfId, // IMPORTANT for backend/DB
        actor_type: "specific_user",
        step_name: "New Step",
        current_user_id: null,
        current_label: "—",
        new_user_id: null,
      };

      if (index == null || index < 0 || index >= prev.length) return [...prev, row];
      const next = prev.slice();
      next.splice(index + 1, 0, row);
      return next;
    });
  };

  const duplicateStep = (key) => {
    setDraft((prev) => {
      const idx = prev.findIndex((x) => x._key === key);
      if (idx === -1) return prev;

      const src = prev[idx];
      const cloneKey = `dup_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      const clone = {
        ...src,
        _key: cloneKey,
        is_new: true,
        step_instance_id: null,
        // keep workflow_step_id + actor_type, reset current label
        current_label: "—",
        current_user_id: null,
        new_user_id: src.new_user_id ?? (src.actor_type === "specific_user" ? src.current_user_id : null),
      };

      const next = prev.slice();
      next.splice(idx + 1, 0, clone);
      return next;
    });
  };

  const moveStep = (key, dir) => {
    setDraft((prev) => {
      const idx = prev.findIndex((x) => x._key === key);
      if (idx === -1) return prev;
      const nextIdx = dir === "up" ? idx - 1 : idx + 1;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;

      const next = prev.slice();
      const tmp = next[idx];
      next[idx] = next[nextIdx];
      next[nextIdx] = tmp;
      return next;
    });
  };

  const removeStep = (key) => {
    setDraft((prev) => {
      if (prev.length <= 1) {
        onError?.("You cannot remove the last remaining step.");
        return prev;
      }
      return prev.filter((x) => x._key !== key);
    });
  };

  const updateRow = (key, patch) => {
    setDraft((prev) => prev.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  };

  const isActorRequiresUser = (actorType) => normalizeActorTypeCanonical(actorType) === "specific_user";

const handleSave = async () => {
  if (!appraisalId) return;

  onError?.("");

  if (hasSubmittedSteps) {
    onError?.("This appraisal already has submitted steps. Customizing workflow is blocked to avoid breaking history.");
    return;
  }

  // Build payload based on current UI order
  const stepsPayload = draft.map((d, idx) => {
    const actorType = normalizeActorTypeCanonical(d.actor_type || "specific_user");

    // ✅ FIX: always keep current user unless explicitly changed
    const rawChosen =
      d.new_user_id != null && String(d.new_user_id).trim() !== ""
        ? Number(d.new_user_id)
        : d.current_user_id != null
        ? Number(d.current_user_id)
        : null;

    const chosenUserId = Number.isFinite(rawChosen) ? rawChosen : null;

    const stepTitle = String(d.step_name || "").trim() || `${roleLabelFromActor(actorType)} Step`;

    const wfIdNum = d.workflow_step_id != null ? Number(d.workflow_step_id) : null;
    const workflowStepId = Number.isFinite(wfIdNum) ? wfIdNum : null;

    return {
      step_order: idx + 1,

      // ✅ backend requires it for ALL steps
      user_id: chosenUserId,

      // ✅ match backend keys
      title: stepTitle,
      role_key: actorType,

      // optional (backend now accepts it if column exists)
      workflow_step_id: workflowStepId,
    };
  });

  // ✅ Strong client-side validation to prevent 422
  const missingUser = stepsPayload.find((s) => !s.user_id);
  if (missingUser) {
    onError?.(`Please assign a user for step #${missingUser.step_order}.`);
    return;
  }

  setSubmitting(true);
  try {
    await api.post(API.customizeWorkflow(appraisalId), {
      steps: stepsPayload,
      reset_progress: true,
    });

    onSaved?.();
    onAfterSave?.();
    onClose?.();
  } catch (err) {
    const v422 = formatLaravel422(err);
    onError?.(v422 || safeMsg(err, "Failed to customize workflow"));
  } finally {
    setSubmitting(false);
  }
};


  return (
    <Dialog open={open} onClose={() => !submitting && onClose?.()} fullWidth maxWidth="md">
      <DialogTitle sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 950, lineHeight: 1.15 }}>
              Customize Workflow
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Add/remove/reorder steps and assign users for this appraisal.
              {employeeLabel ? ` — ${employeeLabel}` : ""}
            </Typography>
          </Box>

          <IconButton onClick={() => !submitting && onClose?.()} sx={{ borderRadius: 2 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Alert
            severity={hasSubmittedSteps ? "error" : "warning"}
            sx={{
              borderRadius: 2,
              bgcolor: alpha(hasSubmittedSteps ? theme.palette.error.main : theme.palette.warning.main, 0.06),
              border: `1px solid ${alpha(hasSubmittedSteps ? theme.palette.error.main : theme.palette.warning.main, 0.18)}`,
            }}
          >
            This creates a <b>custom workflow</b> and regenerates step instances (reset progress).
            {hasSubmittedSteps ? (
              <>
                <br />
                <b>Blocked:</b> steps already submitted.
              </>
            ) : null}
          </Alert>

          <Section>
            {loadingUsers ? <LinearProgress /> : null}

            <Box sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                  Steps
                </Typography>

                <Button
                  onClick={() => addStepAt(null)}
                  disabled={submitting || hasSubmittedSteps}
                  startIcon={<AddCircleOutlineIcon />}
                  sx={{ textTransform: "none", fontWeight: 900, borderRadius: 2 }}
                  variant="outlined"
                >
                  Add step
                </Button>
              </Stack>

              <Stack spacing={1.25}>
                {draft.map((row, idx) => {
                  const stepNo = idx + 1;
                  const actorLabel = roleLabelFromActor(row.actor_type);
                  const requiresUser = isActorRequiresUser(row.actor_type);

                  const chosenId =
                    row.new_user_id != null && String(row.new_user_id).trim() !== ""
                      ? Number(row.new_user_id)
                      : row.current_user_id != null
                      ? Number(row.current_user_id)
                      : null;

                  const selectedOpt = chosenId ? userOptions.find((u) => Number(u.id) === Number(chosenId)) || null : null;

                  const modeChip = (() => {
                    if (row.new_user_id != null && String(row.new_user_id).trim() !== "") return { label: "Fixed", color: theme.palette.success.main };
                    if (requiresUser) return { label: selectedOpt ? "Fixed" : "Required", color: selectedOpt ? theme.palette.success.main : theme.palette.error.main };
                    return { label: selectedOpt ? "Fixed" : "Auto", color: selectedOpt ? theme.palette.success.main : theme.palette.info.main };
                  })();

                  return (
                    <Box
                      key={row._key}
                      sx={{
                        p: 1.6,
                        borderRadius: 12,
                        border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.55),
                      }}
                    >
                      <Stack spacing={1.2}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }} flexWrap="wrap">
                              <Typography variant="body2" sx={{ fontWeight: 950 }} noWrap>
                                Step #{stepNo} — {actorLabel} — {row.step_name || "—"}
                              </Typography>

                              <Chip
                                size="small"
                                label={modeChip.label}
                                sx={{
                                  borderRadius: 999,
                                  fontWeight: 900,
                                  bgcolor: alpha(modeChip.color, 0.10),
                                  color: modeChip.color,
                                  border: `1px solid ${alpha(modeChip.color, 0.22)}`,
                                }}
                              />
                            </Stack>

                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Current: {row.current_label || "—"}
                              {row.workflow_step_id ? ` • workflow_step_id: ${row.workflow_step_id}` : ""}
                            </Typography>
                          </Box>

                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Tooltip title="Move up">
                              <span>
                                <IconButton
                                  onClick={() => moveStep(row._key, "up")}
                                  disabled={submitting || hasSubmittedSteps || idx === 0}
                                  sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.18)}` }}
                                >
                                  <ArrowUpwardIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="Move down">
                              <span>
                                <IconButton
                                  onClick={() => moveStep(row._key, "down")}
                                  disabled={submitting || hasSubmittedSteps || idx === draft.length - 1}
                                  sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.18)}` }}
                                >
                                  <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="Duplicate step">
                              <span>
                                <IconButton
                                  onClick={() => duplicateStep(row._key)}
                                  disabled={submitting || hasSubmittedSteps}
                                  sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}` }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="Insert new step after">
                              <span>
                                <IconButton
                                  onClick={() => addStepAt(idx)}
                                  disabled={submitting || hasSubmittedSteps}
                                  sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}` }}
                                >
                                  <AddCircleOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="Remove step">
                              <span>
                                <IconButton
                                  onClick={() => removeStep(row._key)}
                                  disabled={submitting || hasSubmittedSteps || draft.length <= 1}
                                  sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
                                    bgcolor: alpha(theme.palette.error.main, 0.05),
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </Box>

                        <Divider />

                        <Grid container spacing={1.25} alignItems="center">
                          <Grid item xs={12} md={4}>
                            <TextField
                              select
                              label="Actor type"
                              value={normalizeActorTypeCanonical(row.actor_type || "specific_user")}
                              onChange={(e) => updateRow(row._key, { actor_type: e.target.value })}
                              fullWidth
                              disabled={submitting || hasSubmittedSteps}
                            >
                              {actorTypeOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12} md={8}>
                            <TextField
                              label="Step title"
                              value={row.step_name || ""}
                              onChange={(e) => updateRow(row._key, { step_name: e.target.value })}
                              fullWidth
                              disabled={submitting || hasSubmittedSteps}
                              placeholder="e.g. Draft Evaluation"
                            />
                          </Grid>

                          <Grid item xs={12}>
                            {userOptions.length > 0 ? (
                              <Autocomplete
                                options={userOptions}
                                value={selectedOpt}
                                onChange={(_, val) => updateRow(row._key, { new_user_id: val ? val.id : null })}
                                isOptionEqualToValue={(o, v) => Number(o.id) === Number(v.id)}
                                getOptionLabel={(o) => o.label}
                                disabled={loadingUsers || submitting || hasSubmittedSteps}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={requiresUser ? "Assign specific user (required)" : "Assign specific user (optional)"}
                                    placeholder="Select user"
                                    helperText={
                                      requiresUser
                                        ? "This step is 'Specific User' so a user must be assigned."
                                        : "Leave empty for auto-resolve based on actor type (or keep current)."
                                    }
                                  />
                                )}
                              />
                            ) : (
                              <TextField
                                label={requiresUser ? "Assign User ID (required)" : "Assign User ID (optional)"}
                                type="number"
                                value={row.new_user_id ?? ""}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  updateRow(row._key, { new_user_id: v === "" ? null : Number(v) });
                                }}
                                helperText={
                                  requiresUser
                                    ? "User list could not load. Enter numeric users.id (required for Specific User)."
                                    : "User list could not load. Enter numeric users.id or leave empty to keep current/auto."
                                }
                                fullWidth
                                disabled={submitting || hasSubmittedSteps}
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Section>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none", fontWeight: 900 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={submitting || !appraisalId || hasSubmittedSteps}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 2.2,
            fontWeight: 950,
            boxShadow: "none",
          }}
        >
          {submitting ? "Saving..." : "Save Custom Workflow"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
